import produce, { Draft } from 'immer';
import {
    IGetAllProjects,
    IGetFeedResponse,
    IGetProjects,
    IGetSingleProject,
    IProjectInitialState,
    IReactToComment,
    IRetryProjectProcesing,
    nebulaSuggestion,
    types,
} from './types';
import { types as contestType } from '../contest/types';
import { getIdForIbjects, makeArrayUniqueAndClean, restoreInitialState } from '../../utils/AppUtils';
import { ILikeResponse } from '../account/types';
import { IErrorType } from '../../constants/types';
import { types as storeType } from '../refreshToken/types';
import { types as followTypes } from '../audience/types';
import { IFollowThspian } from '../audience/model';
import { generateTemporaryFollowObject } from '../audience/action';
import { IToggleCreator, types as configurationType } from '../appConfigurator/types';
import { types as contestTypes } from '../contest/types';
import { types as uploadTypes } from '../upload/types';

const initialState: IProjectInitialState = {
    gettingProjects: false,
    projectErrMsg: null,
    projects: [],
    userProjects: [],
    selectedProjectComments: [],
    loading: false,
    playlist: [],
    isSequential: false,
    isSinglePreview: false,
    playlistType: 'recommendation',
    nebulaSuggestion: nebulaSuggestion,
    selectedSuggestion: nebulaSuggestion[0],
    feed: [],
    gettingFeedErrMsg: null,
    currentFeedLimit: 10,
    currentFeedPage: 1,
    canLoadMoreFeed: true,
    triggeringMediaForge: false,
    retries: {},
    gettingSelectedProjectDetails: false,
    verticalPlayer: 'off',
    horizontalPlayer: 'off',
    thspianPlaylist: [],
    userSounds: [],
    currentPlaylistPagination: {
        currentPage: 1,
        hasNextPage: false,
        pageSize: 20,
        totalPages: 1,
    },
};

const reducer = (state: IProjectInitialState = initialState, action: any) => {
    const { type, payload } = action;

    // Helper function to recursively find and update a comment at any depth
    const findAndUpdateComment = (comments: any[], targetId: string, updater: (comment: any) => void): boolean => {
        for (let i = 0; i < comments.length; i++) {
            if (comments[i]._id === targetId) {
                updater(comments[i]);
                return true;
            }
            if (comments[i].replies && comments[i].replies.length > 0) {
                if (findAndUpdateComment(comments[i].replies, targetId, updater)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Helper function to recursively find and remove a comment at any depth
    const findAndRemoveComment = (comments: any[], targetId: string): boolean => {
        for (let i = 0; i < comments.length; i++) {
            if (comments[i]._id === targetId) {
                comments.splice(i, 1);
                return true;
            }
            if (comments[i].replies && comments[i].replies.length > 0) {
                if (findAndRemoveComment(comments[i].replies, targetId)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Helper function to recursively find and add a reply to a comment at any depth
    const findAndAddReply = (comments: any[], parentId: string, newReply: any): boolean => {
        for (let i = 0; i < comments.length; i++) {
            if (comments[i]._id === parentId) {
                if (!comments[i].replies) {
                    comments[i].replies = [];
                }
                comments[i].replies.push(newReply);
                return true;
            }
            if (comments[i].replies && comments[i].replies.length > 0) {
                if (findAndAddReply(comments[i].replies, parentId, newReply)) {
                    return true;
                }
            }
        }
        return false;
    };

    switch (type) {
        case types.GET_PROJECT:
            return produce(state, draftState => {
                const { fromAnotherScreen } = payload as IGetSingleProject;
                draftState.projectErrMsg = null;
                draftState.selectedProject = undefined;
                draftState.selectedProjectDetails = undefined;
                draftState.gettingProject = true;
                if (fromAnotherScreen) {
                    draftState.gettingProjects = true;
                }
            });
        case types.GET_PROJECT_COMMENTS:
            return produce(state, draftState => {
                draftState.projectErrMsg = null;
                draftState.gettingProjectComments = true;
            });
        case types.GET_FEED:
            return produce(state, draftState => {
                draftState.gettingFeed = true;
            });
        case types.GET_FEED_SUCCESS:
            return produce(state, draftState => {
                const { data, page, canLoadMore, firstProjectId } = payload as Draft<IGetFeedResponse>;
                if (page !== undefined && page < 2) {
                    draftState.feed = makeArrayUniqueAndClean(data);
                } else {
                    draftState.feed = makeArrayUniqueAndClean([...draftState.feed, ...data]);
                }
                draftState.gettingFeed = false;
                draftState.currentFeedPage = page;
                draftState.canLoadMoreFeed = canLoadMore;
                draftState.initialProjectId = firstProjectId;
                draftState.upNextPlaylist = data;
                draftState.similarPlaylist = data;
            });
        case types.GET_FEED_FAIL:
            return produce(state, draftState => {
                draftState.gettingFeedErrMsg = payload;
                draftState.gettingFeed = true;
            });
        case types.GET_MY_PROJECTS:
        case types.GET_PROJECTS:
            return produce(state, draftState => {
                const { page, isFiltered, isPlaylistFetch } = (payload as IGetProjects) || {};
                draftState.gettingMoreProjects = true;
                if (page && page <= 1) {
                    if (isFiltered) {
                        draftState.playlist = [];
                    } else {
                        draftState.projects = [];
                    }
                }
                if (isPlaylistFetch) {
                    draftState.gettingPlaylist = true;
                } else {
                    draftState.gettingProjects = true;
                }
                draftState.projectErrMsg = null;
            });
        case types.GET_PROJECT_SUCCESS:
            return produce(state, draftState => {
                /** Rremoved from here since SET_MEDIA handles this */
                // draftState.selectedProject = payload;
                draftState.gettingProject = false;
                /** This allows the shimmer to show on the watch screen
                 * Because when we select a new project to watch. The other projects load but
                 * we only want this effect when navigating from another page
                 */
                draftState.gettingProjects = false;
                draftState.selectedProjectDetails = payload;
                draftState.gettingSelectedProjectDetails = false;
            });
        case types.GET_MY_PROJECTS_FAIL:
        case types.GET_PROJECTS_FAIL:
            return produce(state, draftState => {
                draftState.gettingMoreProjects = false;
            });
        case types.GET_MY_PROJECTS_SUCCESS:
            return produce(state, draftState => {
                draftState.userProjects = payload;
                draftState.gettingProjects = false;
                draftState.gettingMoreProjects = false;
                draftState.gettingPlaylist = false;
            });
        case types.GET_PROJECT_COMMENTS_SUCCESS:
            return produce(state, draftState => {
                draftState.selectedProjectComments = payload;
                draftState.gettingProjectComments = false;
            });
        case types.GET_PROJECTS_SUCCESS:
            return produce(state, draftState => {
                const { data, isFiltered, currentPage, loadMore, currentParam, isPlaylistFetch, isFeedFetch } =
                    payload as Draft<IGetAllProjects>;
                if (currentPage !== undefined && currentPage < 2) {
                    draftState.playlist = data;
                } else if (isFiltered) {
                    draftState.playlist = [...draftState.playlist, ...data];
                } else {
                    draftState.playlist = data;
                }
                if (isFeedFetch) {
                    draftState.feed = data;
                }

                draftState.loadMore = loadMore;
                draftState.currentPage = currentPage;
                draftState.currentParam = currentParam;
                draftState.gettingMoreProjects = false;
                const selectedId = draftState.selectedProjectDetails?._id;
                const selectedIndex = data.findIndex(
                    (project: { _id: string | undefined }) => project._id === selectedId,
                );
                if (selectedIndex > -1) {
                    const afterSelected = data.slice(selectedIndex + 1);
                    const beforeSelected = data.slice(0, selectedIndex);
                    draftState.similarPlaylist = afterSelected.concat(beforeSelected);
                    draftState.upNextPlaylist = afterSelected.concat(beforeSelected);
                } else {
                    draftState.similarPlaylist = data;
                    draftState.upNextPlaylist = data;
                }
                if (isPlaylistFetch) {
                    draftState.gettingPlaylist = false;
                } else {
                    draftState.gettingProjects = false;
                }
            });
        case types.GET_PROJECT_FAIL:
            return produce(state, draftState => {
                draftState.projectErrMsg = payload;
                draftState.gettingProject = false;
                draftState.gettingProjects = false;
            });
        case types.GET_PROJECT_COMMENTS_FAIL:
            return produce(state, draftState => {
                draftState.projectErrMsg = payload;
                draftState.gettingProjectComments = false;
            });
        case types.GET_PROJECT_DETAILS:
            return produce(state, draftState => {
                const { noPlaylistRefetch } = payload as IGetSingleProject;
                draftState.gettingSelectedProjectDetails = true;
                if (!noPlaylistRefetch) {
                    draftState.gettingMoreProjects = true;
                    draftState.gettingPlaylist = true;
                }
            });
        case types.GET_PROJECT_DETAILS_SUCCESS:
            return produce(state, draftState => {
                draftState.selectedProjectDetails = payload;
                draftState.userInteraction = {
                    is_user_liked: payload?.is_user_liked || false,
                    userLike: payload?.user_like && payload?.user_like?.length > 0 ? payload?.user_like : [],
                };
                draftState.gettingSelectedProjectDetails = false;
                const selectedId = payload._id;
                if (draftState.upNextPlaylist) {
                    const selectedIndex = draftState.upNextPlaylist.findIndex(project => project._id === selectedId);
                    if (selectedIndex > -1) {
                        const afterSelected = draftState.upNextPlaylist.slice(selectedIndex + 1);
                        const beforeSelected = draftState.upNextPlaylist.slice(0, selectedIndex);
                        draftState.upNextPlaylist = afterSelected.concat(beforeSelected);
                        draftState.similarPlaylist = afterSelected.concat(beforeSelected);
                    }
                }
                draftState.gettingPlaylist = false;
            });
        case types.GET_PROJECT_DETAILS_FAIL:
            return produce(state, draftState => {
                draftState.projectErrMsg = payload;
                draftState.gettingSelectedProjectDetails = false;
            });
        case types.SET_MEDIA:
            return produce(state, draftState => {
                draftState.projectErrMsg = null;
                draftState.actsErrMsg = null;
                draftState.gettingFeedErrMsg = null;
                const { media, mediaType, submissionObject, playlistType, clearCustomSuggestion, onlySubmissions } =
                    payload as any;

                if (onlySubmissions) {
                    draftState.selectedSubmission = submissionObject;
                } else {
                    if (mediaType === 'act') {
                        draftState.selectedAct = media as any;
                        draftState.horizontalPlayer = 'off';
                        draftState.verticalPlayer = 'on';
                        draftState.selectedProject = media as any;
                    }

                    if (mediaType === 'video') {
                        /** We might be callling thsi twice. Please check the  */
                        draftState.selectedProject = media as any;
                        draftState.verticalPlayer = 'off';
                        draftState.horizontalPlayer = 'on';
                        draftState.selectedAct = media as any;
                    }

                    if (playlistType) {
                        draftState.playlistType = playlistType;
                    }

                    if (clearCustomSuggestion) {
                        draftState.nebulaSuggestion = draftState.nebulaSuggestion?.filter(
                            suggestion => !suggestion.isCustom,
                        );
                        draftState.selectedSuggestion = nebulaSuggestion[0];
                    }

                    draftState.userInteraction = {
                        is_user_liked: media?.is_user_liked || false,
                        userLike: media?.user_like && media?.user_like?.length > 0 ? media?.user_like : [],
                    };

                    draftState.selectedSubmission = submissionObject;
                    draftState.selectedMediaType = mediaType;
                    draftState.gettingProjects = false;
                    draftState.gettingProject = false;
                    draftState.gettingPlaylist = false;
                    if (media === null) {
                        draftState.selectedSuggestion = undefined;
                        draftState.selectedAct = undefined;
                        draftState.selectedProject = undefined;
                        draftState.isPlaylistCollection = false;
                        draftState.currentPlaylist = undefined;
                        draftState.playlist = [];
                        draftState.playlistEditMode = false;
                    }
                }
            });
        case types.SET_SUBMISSION:
            return produce(state, draftState => {
                draftState.selectedSubmission = payload;
            });
        case types.GET_WATCH_CONTENT:
            return produce(state, draftState => {
                draftState.gettingWatchContent = true;
            });
        case types.GET_WATCH_CONTENT_SUCCESS:
            return produce(state, draftState => {
                draftState.watchContent = payload;
                draftState.gettingWatchContent = false;
            });
        case types.GET_WATCH_CONTENT_FAIL:
            return produce(state, draftState => {
                draftState.gettingWatchContentErrMsg = payload;
                draftState.gettingWatchContent = false;
            });
        case types.GET_ACT:
        case types.GET_ACTS:
            return produce(state, draftState => {
                draftState.gettingActs = true;
                draftState.actsErrMsg = null;
            });
        case types.GET_ACT_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingActs = false;
                draftState.selectedAct = payload;
            });
        case types.GET_ACTS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingActs = false;
                draftState.actsPlaylist = payload;
            });
        case types.GET_ACT_FAIL:
        case types.GET_ACTS_FAIL:
            return produce(state, draftState => {
                draftState.gettingActs = false;
                draftState.actsErrMsg = payload;
            });
        case types.GET_COMMENTS_FOR_ACT:
            return produce(state, draftState => {
                draftState.gettingActsComments = true;
            });
        case types.GET_COMMENTS_FOR_ACT_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingActsComments = false;
                draftState.selectedActsComments = payload;
            });
        case types.GET_COMMENTS_FOR_ACT_FAIL:
            return produce(state, draftState => {
                draftState.gettingActsComments = false;
                draftState.gettingActsCommentsErrMsg = payload;
            });
        case types.UDPATE_PROJECT_FIELD:
            return produce(state, draftState => {
                draftState.projectInCreation = {
                    ...draftState.projectInCreation,
                    payload,
                };
            });
        case types.CREATE_PROJECT:
        case types.UPDATE_PROJECT:
        case types.PUBLISH_PROJECT:
        case types.UNPUBLISH_PROJECT:
        case types.UPLOAD_PROJECT_MEDIA:
            return produce(state, draftState => {
                draftState.creatingProject = true;
            });
        case types.UPLOAD_PROJECT_MEDIA_FAIL:
            return produce(state, draftState => {
                draftState.creatingProject = false;
            });
        case types.UPLOAD_PROJECT_MEDIA_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingProject = false;
            });
        case types.CREATE_PROJECT_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingProject = false;
                draftState.currentProjectInCreation = payload;
                draftState.userProjects = makeArrayUniqueAndClean([payload, ...draftState.userProjects]);
                if (draftState.mediaForge) {
                    draftState.mediaForge['progress'] = 0;
                }
            });
        case types.UPDATE_PROJECT_SUCCESS:
            return produce(state, draftState => {
                const response: any = payload;
                const findIndex = draftState.userProjects.findIndex(el => el._id === response._id);
                draftState.userProjects[findIndex] = response;
                draftState.currentProjectInCreation = response;
                if (draftState.currentlyManagedProject) {
                    draftState.currentlyManagedProject = {
                        ...draftState.currentlyManagedProject,
                        ...response,
                    };
                }
                draftState.creatingProject = false;
                draftState.replacingInProgress = false;
            });
        case types.UNPUBLISH_PROJECT_SUCCESS:
            return produce(state, draftState => {
                const response: any = payload;
                const findIndex = draftState.userProjects.findIndex(el => el._id === response._id);
                draftState.userProjects[findIndex] = response;
                draftState.currentProjectInCreation = undefined;
                draftState.creatingProject = false;
            });
        case types.PUBLISH_PROJECT_SUCCESS:
            return produce(state, draftState => {
                const response: any = payload;
                const findIndex = draftState.userProjects.findIndex(el => el._id === response._id);
                draftState.userProjects[findIndex] = response;
                draftState.creatingProject = false;
            });
        case types.CREATE_PROJECT_FAIL:
        case types.PUBLISH_PROJECT_FAIL:
        case types.UNPUBLISH_PROJECT_FAIL:
        case types.UPDATE_PROJECT_FAIL:
            return produce(state, draftState => {
                draftState.creatingProject = false;
                draftState.projectErrMsg = payload;
            });
        case types.DELETE_PROJECT_SUCCESS:
            return produce(state, draftState => {
                const { documentId, additionalPayload } = payload as any;
                const filterProjects = draftState.userProjects.filter(proj => proj._id !== documentId);
                draftState.userProjects = filterProjects;
                if (additionalPayload) {
                    draftState.creatingProject = false;
                }
            });
        case types.DELETE_PROJECT_FAIL:
            return produce(state, draftState => {
                draftState.creatingProject = false;
                draftState.projectErrMsg = payload;
                draftState.creatingProject = false;
            });
        // case types.PUBLISH_PROJECT_SUCCESS:
        //     return produce(state, draftState => {
        //         /** Index of teh project being published */
        //         const index = draftState.userProjects.indexOf(payload);
        //         draftState.userProjects[index] = payload;
        //     });
        case contestType.SUBMIT_PROJECT_SUCCESS:
            return produce(state, draftState => {
                const { project } = payload as any;
                const selectedProjectIndex = draftState.userProjects.findIndex(p => p._id === project._id);
                if (selectedProjectIndex > -1) {
                    const updatedObject = { ...draftState.userProjects[selectedProjectIndex] };
                    updatedObject.submissions?.push(payload);
                    const clonedUserProjects = [...draftState.userProjects];
                    clonedUserProjects[selectedProjectIndex] = updatedObject;
                    draftState.userProjects = clonedUserProjects;
                }
            });
        case contestType.RESCIND_SUBMISSION_SUCCESS:
            return produce(state, draftState => {
                const { project, _id } = payload as any;
                const selectedProjectIndex = draftState.userProjects.findIndex(p => p._id === project._id);
                if (selectedProjectIndex > -1) {
                    const updatedObject = { ...draftState.userProjects[selectedProjectIndex] };
                    updatedObject.submissions = updatedObject.submissions?.filter(sub => sub._id !== _id);
                    const clonedObject = [...draftState.userProjects];
                    clonedObject[selectedProjectIndex] = updatedObject;
                    draftState.userProjects = clonedObject;
                }
            });
        case types.LEAVE_COMMENT:
        case types.UPDATE_COMMENT:
        case types.DELETE_COMMENT:
            return produce(state, draftState => {
                draftState.loading = true;
            });
        case types.LEAVE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                const { comment, isReply, parentComment, category } = payload as any;
                if (category === 'project') {
                    if (isReply && parentComment?._id) {
                        // Use recursive helper to find parent comment at any depth
                        findAndAddReply(draftState.selectedProjectComments, parentComment._id, comment);
                    } else {
                        draftState.selectedProjectComments?.unshift(comment);
                    }

                    draftState.watchContent?.forEach(content => {
                        content.categoryContent.forEach(project => {
                            if (project._id === comment.project?._id) {
                                project.total_comments! += 1;
                            }
                        });
                    });
                }
            });
        case types.LEAVE_COMMENT_FAIL:
        case types.UPDATE_COMMENT_FAIL:
        case types.DELETE_COMMENT_FAIL:
            return produce(state, draftState => {
                draftState.loading = false;
                draftState.projectErrMsg = payload;
            });
        case types.DELETE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                const { documentId } = payload as any;
                const comment: any = payload.payload;

                // Use recursive helper to find and remove comment at any depth
                if (payload.payload && payload.payload.parent_comment) {
                    findAndRemoveComment(draftState.selectedProjectComments, documentId);
                } else {
                    draftState.selectedProjectComments = draftState.selectedProjectComments?.filter(
                        comment => comment._id !== documentId,
                    );
                }

                draftState.watchContent?.forEach(content => {
                    content.categoryContent.forEach(project => {
                        if (project._id === getIdForIbjects(comment.project)) {
                            project.total_comments! -= 1;
                        }
                    });
                });

                draftState.loading = false;
            });
        case types.UPDATE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                /** Type of TComment `addittionalPaload` */
                const { documentId, additionalPayload } = payload as any;
                if (additionalPayload.parent_comment) {
                    // Use recursive helper to find and update comment at any depth
                    findAndUpdateComment(draftState.selectedProjectComments, documentId, comment => {
                        comment.comment = additionalPayload.comment;
                        // TODO: Load the entire object so we can get the updated time
                    });
                } else {
                    const index = draftState.selectedProjectComments?.findIndex(
                        comment => comment._id === additionalPayload._id,
                    );
                    if (index !== -1 && draftState.selectedProjectComments) {
                        draftState.selectedProjectComments[index as unknown as any] = additionalPayload;
                    }
                }
                draftState.loading = false;
            });
        case types.RESET_PROJECT_DATA:
            return restoreInitialState(state, initialState);
        case types.PROJECT_PROCESSING_STATUS:
            return produce(state, draftState => {
                draftState.mediaForge = payload;
            });
        case types.CANCEL_UPLOAD:
            return produce(state, draftState => {
                draftState.mediaForge = undefined;
                draftState.creatingProject = false;
            });
        case types.LIKE_COMMENT:
            return produce(state, draftState => {
                const { commentId } = payload as IReactToComment;
                draftState.liking = true;
                // Use recursive helper to find and update comment at any depth
                findAndUpdateComment(draftState.selectedProjectComments, commentId, comment => {
                    comment.is_user_liked = true;
                });
            });
        case types.LIKE_COMMENT_SUCCESS:
            return produce(state, draftState => {
                const { data, documentId } = payload as ILikeResponse;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                if (documentId) {
                    findAndUpdateComment(draftState.selectedProjectComments, documentId, comment => {
                        comment.is_user_liked = true;
                        comment.user_like = [data as any];
                    });
                }
            });
        case types.LIKE_COMMENT_FAIL:
            return produce(state, draftState => {
                const { documentId, data } = payload as IErrorType;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                if (documentId) {
                    findAndUpdateComment(draftState.selectedProjectComments, documentId, comment => {
                        comment.is_user_liked = false;
                        comment.user_like = [];
                    });
                }
                draftState.projectErrMsg = data;
            });
        case types.DELETE_COMMENT_LIKE:
            return produce(state, draftState => {
                const { additionalPayload } = payload as any;
                draftState.liking = true;
                // Use recursive helper to find and update comment at any depth
                if (additionalPayload?._id) {
                    findAndUpdateComment(draftState.selectedProjectComments, additionalPayload._id, comment => {
                        comment.is_user_liked = false;
                    });
                }
            });
        case types.DELETE_COMMENT_LIKE_SUCCESS:
            return produce(state, draftState => {
                const { additionalPayload } = payload as any;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                if (additionalPayload?._id) {
                    findAndUpdateComment(draftState.selectedProjectComments, additionalPayload._id, comment => {
                        comment.user_like = [];
                    });
                }
            });
        case types.DELETE_COMMENT_LIKE_FAIL:
            return produce(state, draftState => {
                const { documentId } = payload;
                draftState.liking = false;
                // Use recursive helper to find and update comment at any depth
                if (documentId) {
                    findAndUpdateComment(draftState.selectedProjectComments, documentId, comment => {
                        comment.is_user_liked = true;
                    });
                }
            });
        case types.DELETE_A_LIKE:
            return produce(state, draftState => {
                const { additionalPayload } = payload as any;
                draftState.deletingLike = true;
                draftState.userInteraction = {
                    optimistic: true,
                };
                draftState.feed.forEach(item => {
                    if (item._id === getIdForIbjects(additionalPayload)) {
                        item.total_likes! -= 1;
                        item.user_like = [];
                        item.is_user_liked = false;
                    }
                });

                if (draftState.selectedProjectDetails && draftState.selectedProjectDetails.total_likes) {
                    draftState.selectedProjectDetails.total_likes = draftState.selectedProjectDetails.total_likes - 1;
                }
            });
        case types.DELETE_A_LIKE_SUCCESS:
            return produce(state, draftState => {
                const { additionalPayload } = payload as any;
                draftState.userInteraction = {
                    is_user_liked: false,
                    userLike: [],
                    optimistic: false,
                };
                draftState.deletingLike = false;
                draftState.watchContent?.forEach(content => {
                    content.categoryContent.forEach(project => {
                        // Id of the project
                        if (project._id === getIdForIbjects(additionalPayload)) {
                            project.total_likes! -= 1;
                            project.user_like = [];
                            project.is_user_liked = false;
                        }
                    });
                });

                draftState.feed.forEach(item => {
                    if (item._id === getIdForIbjects(additionalPayload)) {
                        item.user_like = [];
                        item.is_user_liked = false;
                    }
                });
            });
        case types.DELETE_A_LIKE_FAIL:
            return produce(state, draftState => {
                draftState.deletingLike = false;
                draftState.userInteraction = {
                    optimistic: false,
                };
                const { documentId, morePayload } = payload;
                draftState.feed.forEach(item => {
                    if (item._id === getIdForIbjects(documentId)) {
                        item.total_likes! += 1;
                        item.user_like = morePayload;
                        item.is_user_liked = true;
                    }
                });
                if (
                    draftState.selectedProjectDetails &&
                    draftState.selectedProjectDetails.total_likes &&
                    draftState.userInteraction.optimistic
                ) {
                    draftState.selectedProjectDetails.total_likes = draftState.selectedProjectDetails.total_likes - 1;
                }
            });
        case types.LIKE_A_PROJECT:
            return produce(state, draftState => {
                const { projectId, like_category, like_type } = payload;
                draftState.liking = true;
                const likedObject: any = {
                    _id: 'optimistic',
                    like_category,
                    user: 'optimistic',
                    project: projectId,
                    optimistic: true,
                    created_at: 'optimistic',
                    created_by: 'optimistic',
                    like_type,
                };
                draftState.userInteraction = {
                    optimistic: true,
                    is_user_liked: true,
                };
                draftState.feed.forEach(item => {
                    if (item._id === projectId) {
                        item.total_likes! += 1;
                        item.user_like = [likedObject];
                        item.is_user_liked = true;
                    }
                });
                if (draftState.selectedProjectDetails && draftState.selectedProjectDetails.total_likes) {
                    draftState.selectedProjectDetails.total_likes = draftState.selectedProjectDetails.total_likes + 1;
                }
            });
        case types.LIKE_A_PROJECT_SUCCESS:
            return produce(state, draftState => {
                const { data } = payload as any;
                draftState.userInteraction = {
                    is_user_liked: true,
                    userLike: [data],
                    optimistic: false,
                };
                draftState.liking = false;
                draftState.watchContent?.forEach(content => {
                    content.categoryContent.forEach(project => {
                        if (project._id === getIdForIbjects(data.project)) {
                            project.total_likes! += 1;
                            project.user_like = [data];
                            project.is_user_liked = true;
                        }
                    });
                });

                draftState.feed.forEach(item => {
                    if (item._id === getIdForIbjects(data.project)) {
                        item.user_like = [data];
                    }
                });
            });
        case types.LIKE_A_PROJECT_FAIL:
            return produce(state, draftState => {
                const { documentId } = payload;
                draftState.liking = false;
                draftState.userInteraction = {
                    optimistic: false,
                    is_user_liked: false,
                };
                draftState.feed.forEach(item => {
                    if (item._id === documentId) {
                        item.total_likes! -= 1;
                        item.user_like = [];
                        item.is_user_liked = false;
                    }
                });
                if (
                    draftState.selectedProjectDetails &&
                    draftState.selectedProjectDetails.total_likes &&
                    draftState.userInteraction.optimistic
                ) {
                    draftState.selectedProjectDetails.total_likes = draftState.selectedProjectDetails.total_likes - 1;
                }
            });
        case types.SET_PLAYER_QUERY:
            return produce(state, draftState => {
                const { isSequential, isSinglePreview } = payload;
                draftState.isSequential = isSequential || false;
                draftState.isSinglePreview = isSinglePreview || false;
            });
        // handle all failed state here when a user is
        case storeType.ENQUEUE_FAILED_ACTIONS:
            //@ts-ignore
            return produce(state, draft => {
                switch (payload.type) {
                    case types.LIKE_A_PROJECT:
                        return produce(state, draftState => {
                            draftState.liking = false;
                            draftState.userInteraction = {
                                optimistic: false,
                                is_user_liked: false,
                            };
                        });
                    case types.LIKE_COMMENT:
                        return produce(state, draftState => {
                            const { commentId } = payload.payload;
                            draftState.liking = false;
                            const index = draftState.selectedProjectComments.findIndex(
                                comment => comment._id === commentId,
                            );
                            if (index !== -1) {
                                draftState.selectedProjectComments[index].is_user_liked = false;
                            }
                        });
                }
            });
        case types.SET_NEBULA_SUGGESTION:
            return produce(state, draft => {
                draft.nebulaSuggestion = [...payload, ...nebulaSuggestion];
                draft.selectedSuggestion = draft.nebulaSuggestion[0];
            });
        case types.SELECT_NEBULA_SUGGESTION:
            return produce(state, draft => {
                draft.selectedSuggestion = payload;
            });
        case followTypes.FOLLOW_A_THSPIAN:
            return produce(state, draftState => {
                const { following } = payload as IFollowThspian;
                const externalProfile =
                    draftState && draftState.selectedProjectDetails && draftState.selectedProjectDetails.created_by;
                if (externalProfile) {
                    const audience = generateTemporaryFollowObject(following);
                    externalProfile.user_following = true;
                    externalProfile.user_follow_interaction?.push(audience as any);
                }
            });
        case followTypes.FOLLOW_A_THSPIAN_SUCCESS:
            return produce(state, draftState => {
                const externalProfile =
                    draftState && draftState.selectedProjectDetails && draftState.selectedProjectDetails.created_by;
                if (externalProfile) {
                    externalProfile.user_follow_interaction = [payload];
                }
            });
        case followTypes.FOLLOW_A_THSPIAN_FAIL:
            return produce(state, draftState => {
                const externalProfile =
                    draftState && draftState.selectedProjectDetails && draftState.selectedProjectDetails.created_by;
                if (externalProfile) {
                    externalProfile.user_follow_interaction = [];
                    externalProfile.user_following = false;
                }
            });
        case followTypes.UNFOLLOW_A_THSPIAN:
            return produce(state, draft => {
                const externalProfile =
                    draft && draft.selectedProjectDetails && draft.selectedProjectDetails.created_by;
                if (externalProfile) {
                    externalProfile.user_follow_interaction = [];
                    externalProfile.user_following = false;
                }
            });
        case followTypes.UNFOLLOW_A_THSPIAN_SUCCESS:
            return produce(state, draft => {});
        case followTypes.UNFOLLOW_A_THSPIAN_FAIL:
            return produce(state, draft => {
                const { following } = payload as IFollowThspian;
                const externalProfile =
                    draft && draft.selectedProjectDetails && draft.selectedProjectDetails.created_by;
                if (externalProfile) {
                    const audience = generateTemporaryFollowObject(following);
                    externalProfile.user_following = true;
                    externalProfile.user_follow_interaction?.push(audience as any);
                }
            });
        case types.DOWNLOAD_PROJECT:
            return produce(state, draft => {
                draft.downloading = true;
            });
        case types.DOWNLOAD_PROJECT_SUCCESS:
            return produce(state, draft => {
                draft.downloading = false;
            });
        case types.DOWNLOAD_PROJECT_FAIL:
            return produce(state, draft => {
                draft.downloading = false;
            });
        case types.TRIGGER_MEDIA_FORGE:
            return produce(state, draft => {
                draft.triggeringMediaForge = true;
            });
        case types.TRIGGER_MEDIA_FORGE_FAIL:
        case types.TRIGGER_MEDIA_FORGE_SUCCESS:
            return produce(state, draft => {
                draft.triggeringMediaForge = false;
            });
        case types.RETRY_PROJECT_PROCESSING:
            return produce(state, draft => {
                const { projectId, timeRetried } = payload as IRetryProjectProcesing;
                const record = {
                    ...draft.retries,
                    [projectId]: timeRetried,
                };
                draft.retries = record;
            });
        case configurationType.TOGGLE_CREATOR_MODAL:
            return produce(state, draft => {
                const { location } = payload as IToggleCreator;
                draft.location = location;
            });
        case contestTypes.SUBMIT_PROJECT_SUCCESS:
        case contestTypes.SUBMIT_PROJECT_FAIL:
            return produce(state, draft => {
                draft.location = undefined;
                draft.currentProjectInCreation = undefined;
            });
        case types.SET_AUTOPLAY:
            return produce(state, draft => {
                draft.autoplay = payload;
            });
        case uploadTypes.TERMINATE_UPLOAD:
            return produce(state, draft => {
                draft.mediaForge = undefined;
            });
        case types.CREATE_PLAYLIST:
            return produce(state, draft => {
                draft.creatingPlaylist = true;
            });
        case types.CREATE_PLAYLIST_SUCCESS:
            return produce(state, draft => {
                draft.creatingPlaylist = false;
                draft.currentPlaylistInCreation = payload;
                draft.userPlaylist?.push(payload);
            });
        case types.CREATE_PLAYLIST_FAIL:
            return produce(state, draft => {
                draft.creatingPlaylist = false;
                draft.playlistErrMsg = payload;
            });
        case types.GET_PLAYLIST:
            return produce(state, draft => {
                draft.gettingPlaylist = true;
                draft.verticalPlayer = 'off';
                draft.horizontalPlayer = 'on';
            });
        case types.GET_MY_PLAYLIST:
            return produce(state, draft => {
                draft.gettingPlaylist = true;
            });
        case types.UPDATE_PLAYLIST:
            return produce(state, draft => {
                draft.updatingPlaylist = true;
            });
        case types.GET_PLAYLIST_SUCCESS:
            return produce(state, draft => {
                const { currentPage, results, hasNextPage, pageSize, totalPages } = payload;
                draft.gettingPlaylist = false;
                if (currentPage && currentPage > 1 && draft.currentPlaylist && draft.currentPlaylist.projects) {
                    draft.currentPlaylist.projects = [...(draft.currentPlaylist?.projects ?? []), ...results.projects];
                } else {
                    draft.currentPlaylist = results;
                }
                draft.currentPlaylistPagination = {
                    currentPage,
                    hasNextPage,
                    pageSize,
                    totalPages,
                };
                draft.playlisCurrenttPage = currentPage;
            });
        case types.GET_PLAYLIST_FAIL:
        case types.GET_MY_PLAYLIST_FAIL:
            return produce(state, draft => {
                draft.gettingPlaylist = false;
                draft.playlistErrMsg = payload;
            });
        case types.UPDATE_PLAYLIST_FAIL:
            return produce(state, draft => {
                draft.updatingPlaylist = false;
                draft.playlistErrMsg = payload;
            });
        case types.GET_MY_PLAYLIST_SUCCESS:
            return produce(state, draft => {
                // Paginate this
                draft.userPlaylist = payload;
                draft.gettingPlaylist = false;
            });
        case types.UPDATE_PLAYLIST_SUCCESS:
            return produce(state, draft => {
                const { response, remove, project, id, rearrange } = payload;
                const { _id } = response;
                if (remove) {
                    const filtered = draft.playlist.filter(proj => proj._id !== project);
                    draft.playlist = filtered;
                    if (draft.currentPlaylist) {
                        draft.currentPlaylist['projects'] = filtered;
                    }
                    if (draft.userPlaylist) {
                        draft.userPlaylist = draft.userPlaylist.map((playlist: any) => {
                            if (playlist._id === id) {
                                return {
                                    ...playlist,
                                    projects: playlist.projects.filter((proj: { _id: any }) => proj._id !== project),
                                };
                            } else {
                                return playlist;
                            }
                        });
                    }
                } else {
                    draft.userPlaylist?.forEach((playlist, index) => {
                        if (playlist._id === _id && draft.userPlaylist) {
                            draft.userPlaylist[index] = response;
                        }
                    });
                }

                if (rearrange) {
                    draft.playlist = response.projects;
                    if (draft.currentPlaylist) {
                        draft.currentPlaylist['projects'] = response.projects;
                    }
                }
                draft.updatingPlaylist = false;
                if (draft.currentPlaylist) {
                    draft.currentPlaylist.name = response.name;
                    draft.currentPlaylist.description = response.description;
                    draft.currentPlaylist.updatedAt = response.updatedAt;
                }
            });
        case types.SET_PLAYLIST:
            return produce(state, draft => {
                const { currentPage, results, isEdit, hasNextPage, pageSize, totalPages } = payload;
                const { projects } = results;
                draft.verticalPlayer = 'off';
                draft.selectedProject = results.projects[0] as unknown as any;
                draft.selectedProjectDetails = results.projects[0] as unknown as any;
                draft.horizontalPlayer = 'on';
                draft.isPlaylistCollection = true;
                draft.currentPlaylist = results;
                if (currentPage && currentPage > 1) {
                    draft.playlist = [...draft.playlist, ...projects];
                } else {
                    draft.playlist = projects as unknown as any;
                }
                draft.currentPlaylistPagination = {
                    currentPage,
                    hasNextPage,
                    pageSize,
                    totalPages,
                };
                if (isEdit) {
                    draft.playlistEditMode = true;
                } else {
                    draft.playlistEditMode = false;
                }
            });
        case types.DELETE_PLAYLIST:
            return produce(state, draft => {
                const { documentId } = payload as any;
                draft.deletingPlaylist = true;
                draft.userPlaylist = draft.userPlaylist?.filter(playlist => playlist._id !== documentId);
            });
        case types.DELETE_PLAYLIST_SUCCESS:
            return produce(state, draft => {
                draft.deletingPlaylist = false;
            });
        case types.DELETE_PLAYLIST_FAIL:
            return produce(state, draft => {
                const { additionalPayload } = payload;
                draft.userPlaylist?.push(additionalPayload);
                draft.deletingPlaylist = false;
            });
        case types.SEARCH_USER_PROJECTS:
            return produce(state, draft => {
                draft.searchingUserProjects = true;
            });
        case types.SEARCH_USER_PROJECTS_SUCCESS:
            return produce(state, draft => {
                const { data, type, page } = payload;
                draft.searchingUserProjects = false;
                if (type === 'audio') {
                    draft.userSounds = Number(page) > 1 ? [...(draft.userSounds ?? []), ...data] : data;
                } else {
                    draft.searchUserProjectsResult =
                        Number(page) > 1 ? [...(draft.searchUserProjectsResult ?? []), ...data] : data;
                }
            });
        case types.SEARCH_USER_PROJECTS_FAIL:
            return produce(state, draft => {
                draft.searchUserProjectsError = payload;
                draft.searchingUserProjects = false;
            });
        case types.CREATE_NEW_SOUND:
        case types.UPDATE_SOUND:
            return produce(state, draft => {
                draft.loadingSound = true;
            });
        case types.CREATE_NEW_SOUND_SUCCESS:
            return produce(state, draft => {
                draft.loadingSound = false;
                draft.userProjects.push(payload);
                draft.userSounds?.push(payload);
            });
        case types.UPDATE_SOUND_SUCCESS:
            return produce(state, draft => {
                draft.loadingSound = false;
                draft.selectedSound = payload;
            });
        case types.CREATE_NEW_SOUND_FAIL:
        case types.UPDATE_SOUND_FAIL:
            return produce(state, draft => {
                draft.loadingSound = false;
                draft.projectErrMsg = payload;
            });
        case types.GET_MANAGE_PROJECT:
            return produce(state, draft => {
                draft.gettingProjectToManage = true;
                draft.currentlyManagedProject = undefined;
            });
        case types.GET_MANAGE_PROJECT_SUCCESS:
            return produce(state, draft => {
                draft.gettingProjectToManage = false;
                draft.currentlyManagedProject = payload;
            });
        case types.GET_MANAGE_PROJECT_FAIL:
            return produce(state, draft => {
                draft.gettingProjectToManage = false;
                draft.projectErrMsg = payload;
            });
        case types.ADD_PROJECT_CREDIT:
            return produce(state, draft => {
                // No loading state needed as we handle this optimistically in the UI
            });
        case types.ADD_PROJECT_CREDIT_SUCCESS:
            return produce(state, draft => {
                // Add the new credit to the currently managed project
                if (draft.currentlyManagedProject && draft.currentlyManagedProject.credits) {
                    draft.currentlyManagedProject.credits.push(payload);
                }
            });
        case types.ADD_PROJECT_CREDIT_FAIL:
            return produce(state, draft => {
                draft.projectErrMsg = payload;
            });
        case types.DELETE_PROJECT_CREDIT:
            return produce(state, draft => {
                // No loading state needed as we handle this optimistically in the UI
            });
        case types.DELETE_PROJECT_CREDIT_SUCCESS:
            return produce(state, draft => {
                // Remove the credit from the currently managed project
                if (draft.currentlyManagedProject && draft.currentlyManagedProject.credits) {
                    const { documentId } = payload;
                    draft.currentlyManagedProject.credits = draft.currentlyManagedProject.credits.filter(
                        credit => credit._id !== documentId,
                    );
                }
            });
        case types.DELETE_PROJECT_CREDIT_FAIL:
            return produce(state, draft => {
                draft.projectErrMsg = payload;
            });
        case types.UPDATE_PROJECT_FILE:
            return produce(state, draft => {
                draft.replacingProjectFile = true;
                draft.replacingInProgress = true;
            });
        case types.UPDATE_PROJECT_FILE_SUCCESS:
            return produce(state, draft => {
                /** Replace only the project and the metadata of currenly managed project */
                if (draft.currentlyManagedProject) {
                    draft.currentlyManagedProject.project = payload.project;
                    draft.currentlyManagedProject.metadata = payload.metadata;
                    draft.currentlyManagedProject.resolutions = undefined;
                    draft.currentlyManagedProject.thumbnail = undefined;
                    draft.currentlyManagedProject.hls = undefined;
                    draft.currentlyManagedProject.processing_complete = false;
                }
                draft.replacingProjectFile = false;
            });
        case types.UPDATE_PROJECT_FILE_FAIL:
            return produce(state, draft => {
                draft.replacingProjectFile = false;
                draft.projectErrMsg = payload;
            });
        case types.GET_PROJECT_DETAIL:
            return produce(state, draft => {
                draft.gettingProjectDetail = true;
            });
        case types.GET_PROJECT_DETAIL_SUCCESS:
            return produce(state, draft => {
                draft.gettingProjectDetail = false;
                draft.projectDetail = payload;
            });
        case types.GET_PROJECT_DETAIL_FAIL:
            return produce(state, draft => {
                draft.gettingProjectDetail = false;
                draft.projectErrMsg = payload;
            });
        default:
            return state;
    }
};

export { reducer };
